import { duracaoLegivel, tamanhoLegivel, velocidadeLegivel } from '../../formato';

/**
 * A conta do progresso de um envio: quanto já foi, em que ritmo, quanto falta.
 *
 * Tudo puro e com o relógio injetado, porque o que se quer provar aqui é
 * justamente o que o olho não pega — que a velocidade não pula a cada evento e
 * que o tempo restante não aparece antes de haver base para ele. Com 5 GiB por
 * anexo o envio dura minutos, às vezes horas, e uma estimativa que dança entre
 * "2 min" e "40 min" é pior que nenhuma.
 *
 * ★ O ritmo é uma JANELA, não a média desde o começo e não a diferença entre
 * dois eventos. O `upload.onprogress` do navegador mede o que entrou no
 * buffer do socket, não o que o destino recebeu: os primeiros megabytes
 * "sobem" de uma vez, e só depois o ritmo real aparece. A média desde o início
 * carregaria essa rajada a vida inteira; a diferença entre eventos treme a
 * cada pacote. Os últimos `JANELA_MS` de amostras esquecem a rajada sozinhos e
 * ainda acompanham uma rede que melhora ou piora.
 */

/** Quanto do passado entra na conta do ritmo. */
export const JANELA_MS = 10_000;
/** Abaixo disto de histórico o ritmo é ruído de rajada, e fica escondido. */
export const MINIMO_PARA_RITMO_MS = 2_000;
/** Abaixo disto o TEMPO RESTANTE fica escondido: o primeiro número seria chute. */
export const MINIMO_PARA_ESTIMAR_MS = 4_000;

export interface Amostra {
  /** Milissegundos de um relógio monotônico. */
  t: number;
  /** Bytes enviados até `t`. */
  bytes: number;
}

/** A janela, com a amostra nova no fim e as que já saíram dela descartadas. */
export function acrescentarAmostra(
  amostras: readonly Amostra[],
  nova: Amostra,
  janelaMs = JANELA_MS,
): Amostra[] {
  const corte = nova.t - janelaMs;
  return [...amostras.filter((a) => a.t >= corte), nova];
}

export interface Medida {
  /** `null` = ainda sem base; `0` = a janela inteira sem avançar um byte. */
  bytesPorSegundo: number | null;
  /** `null` = sem base para estimar, ou parado. */
  segundosRestantes: number | null;
}

/** Ritmo e estimativa a partir da janela. */
export function medir(amostras: readonly Amostra[], total: number): Medida {
  const primeira = amostras[0];
  const ultima = amostras[amostras.length - 1];
  if (!primeira || !ultima || amostras.length < 2) {
    return { bytesPorSegundo: null, segundosRestantes: null };
  }
  const decorrido = ultima.t - primeira.t;
  if (decorrido < MINIMO_PARA_RITMO_MS) return { bytesPorSegundo: null, segundosRestantes: null };

  const bytesPorSegundo = Math.max(0, ultima.bytes - primeira.bytes) / (decorrido / 1000);
  // Parado: não há quanto dividir, e "faltam infinitos segundos" não é frase.
  if (bytesPorSegundo === 0) return { bytesPorSegundo: 0, segundosRestantes: null };
  if (decorrido < MINIMO_PARA_ESTIMAR_MS) return { bytesPorSegundo, segundosRestantes: null };
  return {
    bytesPorSegundo,
    segundosRestantes: Math.max(0, total - ultima.bytes) / bytesPorSegundo,
  };
}

/** O que a barra desenha. */
export interface Progresso extends Medida {
  enviados: number;
  total: number;
}

/**
 * O medidor de UM envio. `amostrar()` sem argumento repete o último valor: é o
 * que o relógio de 1 s da tela chama, e é ele que faz o ritmo cair a zero
 * quando o envio PARA — sem isso, o último número bom ficaria na tela para
 * sempre sobre um envio travado.
 */
export function criarMedidor(
  total: number,
  relogio: () => number = () => performance.now(),
): { amostrar: (bytes?: number) => Progresso } {
  let amostras: Amostra[] = [];
  let enviados = 0;
  return {
    amostrar(bytes) {
      // Nunca anda para trás nem passa do total: o navegador conta cabeçalho
      // junto em alguns casos, e 101% na barra é um defeito que ninguém perdoa.
      if (bytes !== undefined) enviados = Math.min(total, Math.max(enviados, bytes));
      amostras = acrescentarAmostra(amostras, { t: relogio(), bytes: enviados });
      return { enviados, total, ...medir(amostras, total) };
    },
  };
}

/** Os pedaços de texto de uma linha de progresso, já em pt-BR. */
export interface TextoDoProgresso {
  /** 0 a 100 para a largura da barra — nunca 100 antes de os bytes acabarem. */
  percentual: number;
  /** `37%` */
  porcentagem: string;
  /** `1,2 GB de 4,8 GB` */
  quantidade: string;
  /** `12,4 MB/s`, `sem avanço` ou vazio enquanto não há base. */
  ritmo: string;
  /** `faltam 5 min` ou vazio. */
  restante: string;
  /** Tudo foi enviado e falta o destino confirmar. */
  finalizando: boolean;
}

/**
 * `finalizando` existe porque a barra chega a 100% ANTES de o envio acabar: o
 * navegador termina de empurrar os bytes, e o servidor ainda fecha o arquivo e
 * responde. Em 5 GiB isso pode demorar, e uma barra cheia que não termina
 * parece travada — então ela diz o que falta.
 */
export function descrever(p: Progresso): TextoDoProgresso {
  const finalizando = p.total > 0 && p.enviados >= p.total;
  const fracao = p.total > 0 ? p.enviados / p.total : 0;
  // `floor`: 99,96% arredondado para 100 diria "terminou" a quem ainda espera.
  const percentual = finalizando ? 100 : Math.min(99, Math.floor(fracao * 100));
  return {
    percentual,
    porcentagem: `${percentual}%`,
    quantidade: `${tamanhoLegivel(p.enviados)} de ${tamanhoLegivel(p.total)}`,
    ritmo:
      finalizando || p.bytesPorSegundo === null
        ? ''
        : p.bytesPorSegundo === 0
          ? 'sem avanço'
          : velocidadeLegivel(p.bytesPorSegundo),
    restante:
      finalizando || p.segundosRestantes === null
        ? ''
        : `faltam ${duracaoLegivel(p.segundosRestantes)}`,
    finalizando,
  };
}
